<template>
  <div v-if="acl">
    <h3 class="mb-4 permissionsTitle">
      {{ $t('actions.shareWithPeopleOrGroup') }}
    </h3>
    <div v-if="!loading">
      <v-autocomplete
        autofocus
        filled
        hide-details
        class="pb-4"
        :dense="dense"
        :placeholder="$t('actions.addPeopleOrGroup')"
        :items="autocompleteItems"
        :no-data-text="$t('actions.noResult')"
        v-model="selectedItem"
        @change="change"
        item-text="text"
        item-value="payload"
      >
        <template v-slot:item="{ item }">
          <div class="autocomplete-item-container">
            <ReflectionInfo
              v-if="item.payload.kind === 'reflection'"
              :title="item.text"
              :template-id="item.payload.item.template_id"
            />
            <AccessorInfo
              v-if="item.payload.kind === 'acl'"
              :accessor="item.payload.item"
              :show-permission="false"
            />
            <span class="autocomplete-label" v-if="item.payload.kind === 'reflection'">
              {{ $t('globals.reflection.single') }}
            </span>
            <span
              class="autocomplete-label"
              v-if="item.payload.kind === 'acl' && item.payload.item.entityType === 'Group'"
            >
              {{ $t('globals.group.single') }}
            </span>
            <span
              class="autocomplete-label"
              v-if="item.payload.kind === 'acl' && item.payload.item.entityType === 'User'"
            >
              {{ $t('globals.user.single') }}
            </span>
          </div>
        </template>
        <template v-slot:selection />
      </v-autocomplete>

      <span class="currentPermissions">
        {{ $t('views.accesses') }} ({{ accessors.length + reflections.length }})
      </span>

      <ScrollBar max-height="276px">
        <AccessorInfo
          v-for="accessor in accessors"
          :key="accessor.entityId"
          :accessor="accessor"
          :removed-icon="accessor.removed"
          :added-icon="accessor.added"
          :closable="true"
          class="mr-4 ml-2"
          @close="removeAccessor(accessor)"
        />
        <ReflectionDocumentCard
          v-for="reflection in reflections" :key="reflection.id"
          class="mr-2"
          :reflection="reflection"
          :added-icon="reflection.toAdd"
          :removed-icon="reflection.toRemove"
          @close="removeReflection(reflection)"
        />
      </ScrollBar>
      <MissingUserPermission
        v-if="document.acl"
        :opened="confirmationDialogOpened"
        :acl="document.acl"
        :new-acl="reflectionsToAddACL"
        :title="$t('extension.footer.sendButton.linkReflection.aclConfirmationDialog.title')"
        :description-user="$t('extension.footer.sendButton.linkReflection.aclConfirmationDialog.descriptionUser')"
        :description-group="$t('extension.footer.sendButton.linkReflection.aclConfirmationDialog.descriptionGroup')"
        :confirm-btn-label="$t('extension.footer.sendButton.linkReflection.aclConfirmationDialog.confirmButtonLabel')"
        :cancel-btn-label="$t('extension.footer.sendButton.linkReflection.aclConfirmationDialog.cancelButtonLabel')"
        @confirm="giveAccessAndSave"
        @cancel="cancel"
      />

      <div class="d-flex align-center mt-4">
        <v-spacer v-if="showButtons" />
        <div v-if="showButtons" class="d-flex justify-end align-center">
          <v-btn text small class="align-center" color="primary" @click="cancel">
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn small elevation="0" class="ml-4" color="primary" @click="save">
            {{ $t('actions.save') }}
          </v-btn>
          <div v-if="error" class="error--text my-3 pa-2">
            {{ $t('errors.errorOccuredContactUsIfPersists') }}
          </div>
        </div>
      </div>
    </div>
    <div v-else class="loading">
      <v-progress-circular
        size="48"
        indeterminate
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { types as userTypes } from '../../store/user';
import { types as aclEditorTypes } from '../../store/aclEditor';
import { buildAclObject } from '../../store/aclHelper';
import AccessorInfo from '../avatar/AccessorInfo';
import ScrollBar from '../scrollbar/ScrollBar';
import ReflectionDocumentCard from '../reflection/ReflectionDocumentCard';
import MissingUserPermission from '../permission/MissingUserPermission';
import ReflectionInfo from '../reflection/ReflectionInfo';

export default {
  name: 'ACLEditor',
  components: {
    AccessorInfo,
    ScrollBar,
    ReflectionDocumentCard,
    MissingUserPermission,
    ReflectionInfo,
  },
  data: () => ({
    loading: true,
    selectedItem: undefined,
    accessors: [],
    error: false,
    showInPersonalPortfolio: true,
    confirmationDialogOpened: false,
  }),

  props: {
    document: {
      type: Object,
      required: true,
    },
    onPreparedAnalysis: {
      type: Boolean,
      default: false,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      colleagues: userTypes.getters.colleagues,
      groups: userTypes.getters.groups,
      userId: userTypes.getters.userId,
      acl: aclEditorTypes.getters.acl,
      aclAdded: aclEditorTypes.getters.aclAdded,
      aclRemoved: aclEditorTypes.getters.aclRemoved,
      linkedReflections: aclEditorTypes.getters.linkedReflections,
      unlinkedReflections: aclEditorTypes.getters.unlinkedReflections,
      reflectionsToLink: aclEditorTypes.getters.reflectionsToLink,
      reflectionsToUnlink: aclEditorTypes.getters.reflectionsToUnlink,
    }),
    reflectionsToAddACL() {
      let reflectionsToAddACL = [];

      this.reflections.forEach(reflection => {
        const userAcl = reflection.acl
          .filter(acl => acl.entityType === 'User')
          .map(acl => {
            const foundColleague = this.colleagues.find(c => c.id === acl.entityId);
            if (!foundColleague) return null;

            const { firstName, lastName } = foundColleague;
            const displayName = `${firstName} ${lastName}`;
            return { ...acl, displayName };
          }).filter(acl => !!acl);

        const groupAcl = reflection.acl
          .filter(acl => acl.entityType === 'Group')
          .map(acl => {
            const foundGroup = this.groups.find(g => g.id === acl.entityId);
            if (!foundGroup) return null;

            const { name } = foundGroup;
            return { ...acl, displayName: name };
          }).filter(acl => !!acl);

        reflectionsToAddACL = reflectionsToAddACL.concat(userAcl).concat(groupAcl);
      });

      return reflectionsToAddACL;
    },
    reflections() {
      const reflections = this.linkedReflections
        .map((reflection) => {
          const toAdd = !!this.reflectionsToLink.find(r => r.id === reflection.id);
          const toRemove = !!this.reflectionsToUnlink.find(r => r.id === reflection.id);

          return {
            ...reflection,
            toAdd,
            toRemove,
          };
        })
        .concat(this.reflectionsToUnlink
          .filter(reflection => !this.linkedReflections.find(r => reflection.id === r.id))
          .map(reflection => ({ ...reflection, toAdd: false, toRemove: true })))
        .concat(this.reflectionsToLink.map(reflection => ({ ...reflection, toAdd: true, toRemove: false })));

      reflections.sort((a, b) => a.title.localeCompare(b.title));

      return reflections;
    },
    autocompleteItems() {
      const aclItemMapper = (aclItem) => ({
        key: aclItem.acl.entityId,
        text: aclItem.acl.displayName,
        payload: {
          kind: 'acl',
          item: aclItem.acl,
        },
      });

      const aclItemFilter = (aclItem) => !(this.acl.find(acl => acl.entityId === aclItem.key)
          || this.aclAdded.has(aclItem.key)
          || this.aclRemoved.has(aclItem.key));

      const accessorsGroup = this.groups
        .map((g) => ({ acl: buildAclObject('Group', g.id, g.name) }))
        .map(aclItemMapper)
        .filter(aclItemFilter);

      const accessorsUser = this.colleagues.map((c) => ({ acl: buildAclObject('User', c.id,
        `${c.firstName} ${c.lastName}`) }));

      // A colleague has a group id, therefore the same person can appear multiple times in the
      // colleagues list with different group id.
      // However, we only need to keep one ACL entry per unique person and not for each group we
      // have in commun.
      const entityId2Position = new Map();
      accessorsUser.forEach((acl, index) => entityId2Position.set(acl.acl.entityId, index));
      const accessorsUserUnique = Array.from(entityId2Position.values())
        .map((index) => accessorsUser[index])
        .map(aclItemMapper)
        .filter(aclItemFilter);

      const autocompleteReflectionItems = this.unlinkedReflections
        .filter(reflection => !this.reflectionsToLink.find(r => r.id === reflection.id))
        .map((reflection) => ({
          key: reflection.id,
          text: reflection.title,
          payload: {
            kind: 'reflection',
            item: reflection,
          },
        }));

      let autocompleteItems = [];

      if (accessorsUserUnique.length > 0) {
        accessorsUserUnique.sort((a, b) => a.text.localeCompare(b.text));
        autocompleteItems = autocompleteItems
          .concat([{ divider: true }, { header: this.$t('globals.user.plural') }])
          .concat(accessorsUserUnique);
      }

      if (accessorsGroup.length > 0) {
        accessorsGroup.sort((a, b) => a.text.localeCompare(b.text));
        autocompleteItems = autocompleteItems
          .concat([{ divider: true }, { header: this.$t('globals.group.plural') }])
          .concat(accessorsGroup);
      }

      if (autocompleteReflectionItems.length > 0) {
        autocompleteReflectionItems.sort((a, b) => a.text.localeCompare(b.text));
        autocompleteItems = autocompleteItems
          .concat([{ divider: true }, { header: this.$t('globals.reflection.plural') }])
          .concat(autocompleteReflectionItems);
      }

      return autocompleteItems;
    },
  },
  async mounted() {
    if (this.onPreparedAnalysis || (this.document && this.document.acl)) {
      await this.reset();
    }
  },
  methods: {
    ...mapActions({
      saveChanges: aclEditorTypes.actions.saveChanges,
      addAcl: aclEditorTypes.actions.addAcl,
      removeAcl: aclEditorTypes.actions.removeAcl,
      init: aclEditorTypes.actions.init,
      linkReflection: aclEditorTypes.actions.linkReflection,
      unlinkReflection: aclEditorTypes.actions.unlinkReflection,
    }),
    async reset() {
      this.loading = true;
      const entityType = this.computeEntityTypeForDoc(this.document);

      await this.init({ acl: this.document.acl, entityType, entityId: this.document.id });

      this.updateAccessors();
      this.loading = false;
    },
    computeEntityTypeForDoc(doc) {
      return doc && doc.documentDetails
        ? 'analysis'
        : 'reflection';
    },
    change() {
      if (!this.selectedItem) return;

      if (this.selectedItem.kind === 'acl') {
        this.addAcl(this.selectedItem.item);
        this.updateAccessors();
      }
      if (this.selectedItem.kind === 'reflection') {
        this.linkReflection(this.selectedItem.item);
      }

      this.$emit('change');
      this.selectedItem = undefined;
    },
    async giveAccessAndSave() {
      this.confirmationDialogOpened = false;
      this.save();
    },
    startSaveProcess() {
      if (this.reflectionsToAddACL.length > 0) {
        this.confirmationDialogOpened = true;
        return;
      }

      this.save();
    },
    async save() {
      await this.saveChanges(this.document);
      this.reset();

      this.error = false;

      this.$emit('saved');
      this.$emit('close');
    },
    cancel() {
      this.error = false;
      this.confirmationDialogOpened = false;
      this.$emit('cancel');
    },
    removeAccessor(accessor) {
      this.removeAcl(accessor);
      this.updateAccessors();
      this.$emit('change');
    },
    removeReflection(reflection) {
      this.unlinkReflection(reflection);
      this.$emit('change');
    },
    updateAccessors() {
      const accessors = Array.from(this.aclAdded.values())
        .concat(this.acl)
        .filter(acl => !!acl)
        .map(accessor => ({
          ...accessor,
          added: this.aclAdded.has(accessor.entityId),
          removed: this.aclRemoved.has(accessor.entityId),
        }));

      const usersACL = accessors.filter(accessor => accessor.entityType === 'User');
      const groupsACL = accessors.filter(accessor => accessor.entityType === 'Group');

      usersACL.sort((a, b) => a.displayName.localeCompare(b.displayName));
      groupsACL.sort((a, b) => a.displayName.localeCompare(b.displayName));

      this.accessors = usersACL.concat(groupsACL);
    },
  },
  watch: {
    // We have to check a lot of conditions befiore updating the ACL editor state.
    //
    // We don't want to update it when nothing meaninfull happenned, because the user
    // selections and focus are all reset, which causes frustation and visual gliches.
    // However, we want to reset it when a new document is selected with different
    // permissions.
    document(newValue, oldValue) {
      const newHasAcl = newValue.acl;
      const oldHasAcl = oldValue.acl;

      if (!newHasAcl || !oldHasAcl) {
        // Missing acl object, updating
        this.reset();
        return;
      }

      const newEntityType = this.computeEntityTypeForDoc(newValue);
      const oldEntityType = this.computeEntityTypeForDoc(oldValue);

      if (newEntityType !== oldEntityType) {
        // Different entity type, updating
        this.reset();
        return;
      }

      const newAclEntityIds = newValue.acl.filter(acl => acl.entityId !== this.userId);
      const oldAclEntityIds = oldValue.acl.filter(acl => acl.entityId !== this.userId);

      if (newAclEntityIds.length === 0 && oldAclEntityIds.length === 0) {
        // Nothing to update
        return;
      }

      this.reset();
    },
  },
};
</script>

<style scoped>
.permissionsTitle {
  font-weight: 500;
  text-align: center;
}

.currentPermissions {
  font-weight: 500;
  font-size: 14px;
  text-align: center;
}

::v-deep .personalPortfolio .v-label {
  font-size: 14px;
}

.autocomplete-item-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(600px - 32px - 16px);
}

.autocomplete-label {
  flex-shrink: 0;
  padding-left: 12px;
  font-size: 12px;
  color: gray;
  font-weight: 400;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100px;
}
</style>
